import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { fetchEplexAccountInfo } from "../../api/eplexAccountInfoApi";
import type { EplexAccountInfo } from "../../models/eplex/AccountInfo";

type AccountInfoStatus = "idle" | "loading" | "ready" | "error";

const TOKEN_BILLING_TYPE = 1;

/**
 * Признаки учётной записи из Eplex API (CAB-733): схема тарификации и участие
 * в онбординге. Один стор на весь кабинет — по той же причине, что и контекст
 * клиента: разведённые по компонентам проверки расходятся при смене признака.
 *
 * Пока сведения не загружены или недоступны — безопасные значения: биллинг
 * скрыт, лимиты видны, наград нет. Ошибка в эту сторону ничего не ломает,
 * обратная показала бы человеку раздел, за которым у него ничего нет.
 */
export const useEplexAccountInfoStore = defineStore("eplexAccountInfo", () => {
  const info = ref<EplexAccountInfo | null>(null);
  const status = ref<AccountInfoStatus>("idle");

  let inflight: Promise<void> | null = null;

  const isResolved = computed(() => status.value === "ready" || status.value === "error");
  const hasTokenBilling = computed(() => status.value === "ready" && info.value?.billingType === TOKEN_BILLING_TYPE);
  const isOnboardingParticipant = computed(() => status.value === "ready" && info.value?.isOnboarding === true);

  async function load(force = false): Promise<void> {
    if (!force && (status.value === "ready" || status.value === "loading")) {
      await (inflight ?? Promise.resolve());
      return;
    }

    // Перечитывание не отбрасывает кабинет в «ещё не знаем»: по этим признакам
    // решается видимость раздела «Биллинг» и лимитов, и заход через `loading`
    // моргал бы разделом на каждой проверке свежести
    const hadInfo = status.value === "ready";
    if (!hadInfo) status.value = "loading";

    inflight = (async () => {
      try {
        info.value = await fetchEplexAccountInfo();
        status.value = "ready";
      } catch (error) {
        console.error("Eplex account info load error: ", error);
        // Сбой перечитывания — не доказательство того, что признаки изменились:
        // держим последние достоверные, следующая проверка попробует снова
        status.value = hadInfo ? "ready" : "error";
      } finally {
        inflight = null;
      }
    })();

    await inflight;
  }

  /**
   * Забывает признаки. Зовётся при выходе и при смене личности под вкладкой:
   * до нового ответа онбординга и биллинга нет ни у кого — показать чужие
   * признаки хуже, чем на секунду не показать свои.
   */
  function reset(): void {
    info.value = null;
    status.value = "idle";
    inflight = null;
  }

  return {
    info,
    status,
    isResolved,
    hasTokenBilling,
    isOnboardingParticipant,
    load,
    reset,
  };
});
